import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  mode: ThemeMode;
  /** Resolved mode after applying system preference */
  resolved: 'light' | 'dark';
}

function systemIsDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolveMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') return systemIsDark() ? 'dark' : 'light';
  return mode;
}

const stored = ((): ThemeMode => {
  try {
    const v = localStorage.getItem('myledger_theme');
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch { /* ignore */ }
  return 'system';
})();

const initialMode: ThemeMode = stored;

const initialState: ThemeState = {
  mode: initialMode,
  resolved: resolveMode(initialMode),
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<ThemeMode>) {
      state.mode = action.payload;
      state.resolved = resolveMode(action.payload);
      try { localStorage.setItem('myledger_theme', action.payload); } catch { /* ignore */ }
    },
    syncSystem(state) {
      if (state.mode === 'system') {
        state.resolved = systemIsDark() ? 'dark' : 'light';
      }
    },
  },
});

export const { setTheme, syncSystem } = themeSlice.actions;
export default themeSlice.reducer;
